import React, { useCallback, useEffect, useMemo, useState } from "react";

import Alert from "/src/util/alert";

import { MERCHANDISER_MAIN_PAGE, ROUTE_CALENDAR } from "../../config/lang";
import { call } from "../../util/action";
import { getStore } from "../../util/redux/store";
import { translate } from "../../util/util";

import PointSearchPanel from "./route/PointSearchPanel";
import RouteCalendar from "./route/RouteCalendar";
import ScopeMenu from "./route/ScopeMenu";
import {
  addPoint,
  desiredRoutesFromLayers,
  isBaseKey,
  isWeekKey,
  layersFromRoutes,
  movePoint,
  ordersForRoute,
  removePoint,
  routeSpecForKey,
} from "./route/routeModel";
import { addMonths, firstOfMonth } from "./route/dateUtils";

import "./route/style.scss";

const callP = body => new Promise(resolve => call(body, resolve));
const enWeekday = wd => new Intl.DateTimeFormat("en", { weekday: "long" }).format(new Date(2024, 0, 1 + wd));
const sameIds = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

const titleForKey = key => {
  if (isBaseKey(key)) return enWeekday(Number(key.slice(1)));
  if (isWeekKey(key)) {
    const [wd, nth] = key.slice(1).split("_").map(Number);
    return `${nth} ${enWeekday(wd)}`;
  }
  return key.slice(1);
};

// Diff the layers against the routes as loaded → routes to (re)create and ids to delete.
const computeReconcile = (layers, loaded, extraDeletedIds) => {
  const desired = desiredRoutesFromLayers(layers);
  const desiredKeys = new Set(desired.map(r => r.key));
  const toDelete = new Set(extraDeletedIds);
  const toCreate = [];
  desired.forEach(({ key, stops }) => {
    const want = stops.map(s => s.point.id);
    const prev = loaded[key];
    if (prev && sameIds(want, prev.pointIds)) return; // unchanged
    if (prev && prev.id) toDelete.add(prev.id); // replace: recreate with new stops/order
    toCreate.push({ key, stops });
  });
  Object.keys(loaded).forEach(key => {
    if (!desiredKeys.has(key) && loaded[key].id) toDelete.add(loaded[key].id);
  });
  return { toCreate, toDelete: [...toDelete] };
};

export default function Route({ user }) {
  const me = getStore().getState().me;
  const lang = me?.language || "en";

  const [layers, setLayers] = useState({ base: {}, week: {}, date: {} });
  const [loaded, setLoaded] = useState({});
  const [extraDeletedIds, setExtraDeletedIds] = useState([]);
  const [monthDate, setMonthDate] = useState(() => firstOfMonth(new Date()));
  const [pendingDrop, setPendingDrop] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!user?.id) return;
    call({ method: "GET", url: "routes?user=" + user.id }, resp => {
      const list = Array.isArray(resp) ? resp : resp?.results || [];
      const { layers: l, loaded: ld, extraDeletedIds: ex } = layersFromRoutes(list);
      setLayers(l);
      setLoaded(ld);
      setExtraDeletedIds(ex);
    });
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const { toCreate, toDelete } = useMemo(
    () => computeReconcile(layers, loaded, extraDeletedIds),
    [layers, loaded, extraDeletedIds]
  );
  const hasChanges = toCreate.length > 0 || toDelete.length > 0;

  const onDrop = (date, point) => setPendingDrop({ date, point });

  const addWithScope = (date, point, scope) => setLayers(l => addPoint(l, scope, date, point));
  const removeStop = (layerKey, pointId) => setLayers(l => removePoint(l, layerKey, pointId));
  const moveStop = (layerKey, pointId, dir) => setLayers(l => movePoint(l, layerKey, pointId, dir));

  const save = async () => {
    if (!hasChanges || saving) return;
    setSaving(true);
    try {
      // Create first, then delete — a failed create leaves old routes intact (recoverable
      // duplicate, consolidated on next load) instead of losing data.
      for (const { key, stops } of toCreate) {
        const spec = routeSpecForKey(key, { userId: user.id, createdBy: me?.id ?? null, title: titleForKey(key) });
        const created = await callP({ method: "POST", url: "routes/", post: spec });
        if (!created || !created.id) throw new Error("route create failed: " + JSON.stringify(created));
        const orders = ordersForRoute(created.id, stops);
        if (orders.length) await callP({ method: "POST", url: "order/", post: orders });
      }
      for (const id of toDelete) {
        await callP({ method: "DELETE", url: `routes/${id}/` }); // orders cascade-delete
      }
      Alert.success(translate(MERCHANDISER_MAIN_PAGE, "data_successfully_saved", lang), { position: "bottom-right" });
      load();
    } catch (e) {
      Alert.warning("Error while saving routes. " + (e?.message || ""), { position: "bottom-right" });
    } finally {
      setSaving(false);
    }
  };

  const stopCount = useMemo(() => {
    let n = 0;
    Object.values(layers.base).forEach(a => (n += a.length));
    Object.values(layers.week).forEach(byNth => Object.values(byNth).forEach(a => (n += a.length)));
    Object.values(layers.date).forEach(a => (n += a.length));
    return n;
  }, [layers]);

  return (
    <div className="rc-root">
      <div className="rc-toolbar">
        <div className="rc-toolbar-info">
          <span className="material-symbols-outlined">calendar_month</span>
          <span>{translate(ROUTE_CALENDAR, "heading", lang)}</span>
          <span className="rc-toolbar-count">{stopCount}</span>
        </div>
        <button className="mp-save-btn" onClick={save} disabled={!hasChanges || saving}>
          <span className="material-symbols-outlined">save</span>
          {translate(MERCHANDISER_MAIN_PAGE, "save_btn", lang)}
        </button>
      </div>

      <div className="rc-layout">
        <PointSearchPanel lang={lang} />
        <RouteCalendar
          monthDate={monthDate}
          layers={layers}
          lang={lang}
          onPrev={() => setMonthDate(m => addMonths(m, -1))}
          onNext={() => setMonthDate(m => addMonths(m, 1))}
          onDrop={onDrop}
          onRemove={removeStop}
          onMove={moveStop}
        />
      </div>

      <div className="rc-legend">{translate(ROUTE_CALENDAR, "legend", lang)}</div>

      {pendingDrop && (
        <ScopeMenu
          date={pendingDrop.date}
          point={pendingDrop.point}
          lang={lang}
          onChoose={scope => {
            addWithScope(pendingDrop.date, pendingDrop.point, scope);
            setPendingDrop(null);
          }}
          onCancel={() => setPendingDrop(null)}
        />
      )}
    </div>
  );
}
